import { CloseIcon, LeftUpArrowIcon, RightUpArrowIcon } from '@mm/icons';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ReactComponent as CircleCheckArea } from 'assets/circle-check-icon.svg';
import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import './Banner.pcss';
import { GreenMarker } from '../GreenMarker/GreenMarker';

type Props = {
  css?: {
    container?: string;
    content?: string;
    header?: string;
    left?: string;
    right?: string;
    collapsedHeader?: string;
    closeIcon?: string;
  };
  title?: string | JSX.Element;
  checkpointsText?: string[];
  submitButtonText?: string;
  onSubmitButtonClick?: () => void;
  submitButtonClassName?: string;
  children: string | JSX.Element | JSX.Element[];
  button?: string | JSX.Element | JSX.Element[];
  showGreenMarker?: boolean;
  leftIcon?: JSX.Element;
  headerIcon?: JSX.Element;
  withArrow?: boolean;
  reverse?: boolean;
  transparentStyle?: boolean;
  arrowColor?: string;
  withBannerHeader?: boolean;
  withHeaderArrowIcon?: boolean;
  headerText?: ReactNode;
  expandedOnMount?: boolean;
  showCloseIcon?: boolean;
  usedInSlideout?: boolean;
  withLeftArea?: boolean;
  contentLoading?: boolean;
  setContentLoading?: (loaded: boolean) => void;
};

export const Banner = ({
  css = { collapsedHeader: '' },
  title,
  checkpointsText = [],
  children,
  showGreenMarker,
  withArrow = false,
  reverse = false,
  headerIcon,
  arrowColor = '#BCE6FF',
  transparentStyle,
  button,
  withBannerHeader = false,
  withHeaderArrowIcon = false,
  headerText,
  expandedOnMount = false,
  showCloseIcon = false,
  usedInSlideout = false,
  withLeftArea = true,
  contentLoading,
  setContentLoading,
}: Props) => {
  const [expandBanner, setExpandBanner] = useState(expandedOnMount);
  const [showBanner, setShowBanner] = useState(true);
  const collapsedClassName =
    css?.collapsedHeader && !expandBanner ? css?.collapsedHeader : '';
  return showBanner ? (
    <>
      {withBannerHeader && (
        <div
          className={classNames(
            'c-banner__header',
            css?.header,
            collapsedClassName,
            {
              'c-banner__header--slideout': usedInSlideout,
            },
          )}
          onClick={() => {
            setExpandBanner(!expandBanner);
            if (setContentLoading && !expandBanner) {
              setContentLoading(true);
            }
          }}
        >
          <span className="c-banner-header-text">
            {headerText}
            {contentLoading && (
              <i className="fas fa-spinner fa-spin c-banner-spinner" />
            )}
          </span>

          {withHeaderArrowIcon && (
            <>
              {expandBanner ? (
                <UpOutlined
                  className="c-banner__header-arrow"
                  color={arrowColor}
                />
              ) : (
                <DownOutlined
                  className="c-banner__header-arrow"
                  color={arrowColor}
                />
              )}
            </>
          )}

          {showCloseIcon && (
            <CloseIcon
              color="white"
              className={classNames('c-banner__close-icon', css.closeIcon)}
              onClick={() => setShowBanner(false)}
            />
          )}
        </div>
      )}
      {(expandBanner || !withBannerHeader) && (
        <div
          className={classNames('c-banner', css?.container, {
            'c-banner--transparent': transparentStyle,
            'c-banner--slideout': usedInSlideout,
            'c-banner--loadingContent': contentLoading,
          })}
        >
          <div
            className={classNames('c-banner__content', css?.content, {
              'c-banner__content--reverse': reverse,
            })}
          >
            {withLeftArea && (
              <div
                className={classNames(
                  'c-banner__check-area-container',
                  css?.left,
                )}
              >
                <div className={classNames('c-banner-header', css?.header)}>
                  {headerIcon && (
                    <div className="c-banner-header__icon">{headerIcon}</div>
                  )}
                  {title}
                </div>
                {checkpointsText.map(text => (
                  <div key={text} className="c-banner__check-area">
                    <CircleCheckArea className="c-banner__check-area-icon" />
                    <div className="c-banner__check-area-text">{text}</div>
                  </div>
                ))}

                {withArrow &&
                  (reverse ? (
                    <LeftUpArrowIcon
                      className="c-banner__arrow-icon"
                      color={arrowColor}
                    />
                  ) : (
                    <RightUpArrowIcon
                      className="c-banner__arrow-icon"
                      color={arrowColor}
                    />
                  ))}

                <div className="c-banner__button-area">
                  {button}
                  {showGreenMarker && (
                    <GreenMarker className="c-banner__green-marker" />
                  )}
                </div>
              </div>
            )}

            <div className={css?.right}>{children}</div>
          </div>
        </div>
      )}
    </>
  ) : null;
};
